/**
 * PrimeNG writes viewport coordinates (`getBoundingClientRect`, `scrollY`, `innerHeight`)
 * straight into `top` / `left`. Those numbers are already scaled by `body` zoom, while
 * `offsetWidth` / `offsetHeight` are not. Dividing only the viewport numbers puts the
 * overlay back on its trigger. At zoom 1 the formulas match PrimeNG.
 */

export interface OverlayViewportInput {
  zoom: number;
  targetTop: number;
  targetLeft: number;
  targetWidth: number;
  targetHeight: number;
  overlayWidth: number;
  overlayHeight: number;
  scrollTop: number;
  scrollLeft: number;
  viewportWidth: number;
  viewportHeight: number;
}

export interface OverlayPosition {
  top: number;
  left: number;
  origin: 'top' | 'bottom';
}

interface ZoomSpace {
  zoom: number;
  targetTop: number;
  targetLeft: number;
  scrollTop: number;
  scrollLeft: number;
  viewportWidth: number;
  viewportHeight: number;
}

function zoomSpace(input: OverlayViewportInput): ZoomSpace {
  const zoom = Number.isFinite(input.zoom) && input.zoom > 0 ? input.zoom : 1;
  return {
    zoom,
    targetTop: input.targetTop / zoom,
    targetLeft: input.targetLeft / zoom,
    scrollTop: input.scrollTop / zoom,
    scrollLeft: input.scrollLeft / zoom,
    viewportWidth: input.viewportWidth / zoom,
    viewportHeight: input.viewportHeight / zoom,
  };
}

/** `appendTo="body"` placement used by Select, DatePicker, Menu, Popover. */
export function absoluteOverlayPosition(input: OverlayViewportInput): OverlayPosition {
  const space = zoomSpace(input);
  let top: number;
  let origin: OverlayPosition['origin'];
  if (space.targetTop + input.targetHeight + input.overlayHeight > space.viewportHeight) {
    top = space.targetTop + space.scrollTop - input.overlayHeight;
    origin = 'bottom';
    if (top < 0) {
      top = space.scrollTop;
    }
  } else {
    top = input.targetHeight + space.targetTop + space.scrollTop;
    origin = 'top';
  }

  const left = space.targetLeft + input.overlayWidth > space.viewportWidth
    ? Math.max(0, space.targetLeft + space.scrollLeft + input.targetWidth - input.overlayWidth)
    : space.targetLeft + space.scrollLeft;

  return {top, left, origin};
}

/** PrimeNG `DomHandler.relativePosition` (`appendTo="self"` on `p-overlay`). */
export function domRelativeOverlayPosition(
  input: OverlayViewportInput & {relativeTop: number; relativeLeft: number},
): OverlayPosition {
  const space = zoomSpace(input);
  const relativeTop = input.relativeTop / space.zoom;
  const relativeLeft = input.relativeLeft / space.zoom;
  let top: number;
  let origin: OverlayPosition['origin'];
  if (space.targetTop + input.targetHeight + input.overlayHeight > space.viewportHeight) {
    top = space.targetTop - relativeTop - input.overlayHeight;
    origin = 'bottom';
    if (space.targetTop + top < 0) {
      top = -space.targetTop;
    }
  } else {
    top = input.targetHeight + space.targetTop - relativeTop;
    origin = 'top';
  }

  const horizontalOverflow = space.targetLeft + input.overlayWidth - space.viewportWidth;
  const targetLeftOffset = space.targetLeft - relativeLeft;
  let left: number;
  if (input.overlayWidth > space.viewportWidth) {
    left = (space.targetLeft - relativeLeft) * -1;
  } else if (horizontalOverflow > 0) {
    left = targetLeftOffset - horizontalOverflow;
  } else {
    left = targetLeftOffset;
  }
  return {top, left, origin};
}

/** `@primeuix/utils` `relativePosition` (DatePicker and Menu when they stay on the trigger). */
export function primeRelativeOverlayPosition(
  input: OverlayViewportInput & {fixedOrigin?: 'top' | 'bottom'},
): OverlayPosition {
  const space = zoomSpace(input);
  let origin: OverlayPosition['origin'] = input.fixedOrigin ?? 'top';
  let top: number;
  if (!input.fixedOrigin && space.targetTop + input.targetHeight + input.overlayHeight > space.viewportHeight) {
    top = -input.overlayHeight;
    origin = 'bottom';
    if (space.targetTop + top < 0) {
      top = -space.targetTop;
    }
  } else {
    top = input.targetHeight;
  }

  let left: number;
  if (input.overlayWidth > space.viewportWidth) {
    left = space.targetLeft * -1;
  } else if (space.targetLeft + input.overlayWidth > space.viewportWidth) {
    left = (space.targetLeft + input.overlayWidth - space.viewportWidth) * -1;
  } else {
    left = 0;
  }
  return {top, left, origin};
}

/** Right-click menu. `pageX` / `pageY` are in the same viewport space as `getBoundingClientRect`. */
export function contextMenuPosition(input: {
  pageX: number;
  pageY: number;
  width: number;
  height: number;
  scrollLeft: number;
  scrollTop: number;
  viewportWidth: number;
  viewportHeight: number;
  zoom: number;
}): {left: number; top: number} {
  const space = zoomSpace({
    zoom: input.zoom,
    targetTop: input.pageY,
    targetLeft: input.pageX,
    targetWidth: 0,
    targetHeight: 0,
    overlayWidth: 0,
    overlayHeight: 0,
    scrollTop: input.scrollTop,
    scrollLeft: input.scrollLeft,
    viewportWidth: input.viewportWidth,
    viewportHeight: input.viewportHeight,
  });
  let left = space.targetLeft + 1;
  let top = space.targetTop + 1;
  if (left + input.width - space.scrollLeft > space.viewportWidth) {
    left -= input.width;
  }
  if (top + input.height - space.scrollTop > space.viewportHeight) {
    top -= input.height;
  }
  if (left < space.scrollLeft) {
    left = space.scrollLeft;
  }
  if (top < space.scrollTop) {
    top = space.scrollTop;
  }
  return {left, top};
}
